import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient from '../../../service/apiClient';
import { toast } from 'react-toastify';
import '../email/EmailTemplateEditor.css';
import { useFranchiseStore } from '../../../store/useFranchise';
import { useIframeStore } from '../../../store/useIframe';
import { useSidebarStore } from '../../../store/useSidebar';
import BizLinkForm from './BizLinkForm';
import {
  Step10TemplateWaitingPage,
  Step11TemplateApprovedPage,
} from './steps';

/**
 * @author 사주톡톡
 * @description 카카오 채널 발신프로필(비즈메시지) 연동 페이지.
 *
 *  [흐름]
 *   - 신규 가맹점(bizmProfileKey 없음): BizLinkForm 단일 페이지에서 연동 (검색용아이디/휴대폰 인증)
 *   - 연동 성공 → step 10 (템플릿 승인 대기) → 전체 승인 시 step 11 (승인 완료)
 *   - 기존 가맹점(bizmProfileKey 있음): 바로 step 10/11
 *
 *  [deprecated] 기존 온보딩 튜토리얼 플로우(steps/Step0~Step9)와
 *  구글시트 세팅(Step12*, Step10OldTemplateManagement)은 더 이상 사용하지 않습니다.
 *  파일은 참고용으로 남겨두되 이 페이지에서 렌더링하지 않습니다. (steps/index.ts 참고)
 */

const SenderProfilePage: React.FC = () => {
  // 0~9: 연동 폼 / 10: 템플릿 승인 대기 / 11: 승인 완료
  const [currentStep, setCurrentStep] = useState(0);
  const [profileKey, setProfileKey] = useState<string | null>(null);
  const [templateStatuses, setTemplateStatuses] = useState<
    Array<{ templateCode: string; approved: boolean }>
  >([]);

  const { closeIframe } = useIframeStore();
  const { setSidebarVisible } = useSidebarStore();
  const { myfranchise, getMyFranchise } = useFranchiseStore();

  // 진입 시 iframe 닫고 사이드바 접기
  useEffect(() => {
    closeIframe();
    setSidebarVisible(false);
    getMyFranchise();
  }, [closeIframe, setSidebarVisible, getMyFranchise]);

  // 가맹점 정보 기준 분기: 기존 연동 여부에 따라 시작 화면 결정
  useEffect(() => {
    if (myfranchise?.bizmProfileKey) {
      setProfileKey(myfranchise.bizmProfileKey);
      setCurrentStep(10);
    } else {
      setCurrentStep(0);
    }
  }, [myfranchise]);

  const fetchTemplateStatuses = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/bizmsg/sender/template/status');
      if (!data || typeof data !== 'object') {
        throw new Error('승인 상태 응답이 올바르지 않습니다.');
      }
      if (String(data.code || '').toLowerCase() !== 'success') {
        throw new Error(data.message || '승인 상태 조회에 실패했습니다.');
      }
      const rawList = Array.isArray(data.data) ? data.data : [];
      const formatted = rawList
        .filter((item: unknown) => item && typeof item === 'object')
        .map((item: any) => ({
          templateCode:
            typeof item.templateCode === 'string' ? item.templateCode : 'UNKNOWN',
          approved:
            typeof item.approved === 'boolean'
              ? item.approved
              : String(item.inspectionStatus || '').toUpperCase() === 'APR',
        }));
      setTemplateStatuses(formatted);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        '템플릿 승인 상태를 불러오는 중 오류가 발생했습니다.';
      toast.error(message);
      setTemplateStatuses([]);
    }
  }, []);

  useEffect(() => {
    if (profileKey) {
      fetchTemplateStatuses();
    } else {
      setTemplateStatuses([]);
    }
  }, [profileKey, fetchTemplateStatuses]);

  const allTemplatesApproved = useMemo(() => {
    if (!templateStatuses.length) return false;
    return templateStatuses.every(item => item.approved);
  }, [templateStatuses]);

  // 연동 성공: profileKey 갱신은 getMyFranchise(BizLinkForm 내부)로 처리됨 → 템플릿 화면으로
  const handleLinked = () => {
    if (myfranchise?.bizmProfileKey) {
      setProfileKey(myfranchise.bizmProfileKey);
    }
    setCurrentStep(10);
  };

  return (
    <div className="email-template-editor" style={{ padding: 0 }}>
      <div className="editor-content" style={{ padding: 0 }}>
        <div className="editor-form" style={{ flex: '1 1 100%' }}>
          <div className="form-section" style={{ padding: 0 }}>
            {currentStep < 10 && (
              <BizLinkForm onSuccess={handleLinked} getMyFranchise={getMyFranchise} />
            )}

            {currentStep === 10 && !allTemplatesApproved && (
              <Step10TemplateWaitingPage
                allTemplatesApproved={allTemplatesApproved}
                onTemplatesApproved={() => setCurrentStep(11)}
              />
            )}

            {(currentStep === 11 ||
              (currentStep === 10 && allTemplatesApproved)) && (
              <Step11TemplateApprovedPage />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SenderProfilePage;
